import React, {
    FC,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import ReactDOM from 'react-dom';

import { noop } from 'lodash/fp';

import { keyframes, styled } from '../theme';
import Backdrop from './Backdrop';

const ModalContext = createContext<{ onHide: () => void } | undefined>(undefined);

export const ModalProvider = ({
    onHide,
    children,
}: {
    onHide: () => void;
    children: React.ReactNode;
}) => {
    return <ModalContext.Provider value={{ onHide }}>{children}</ModalContext.Provider>;
};

export const useModal = () => {
    const context = useContext(ModalContext);
    if (!context) {
        throw new Error('useModal must be used within a ModalProvider');
    }
    return context;
};

const slideDown = keyframes`
    from {
        transform: translateY(-25%);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
`;

const slideUp = keyframes`
    from {
        transform: translateY(0);
        opacity: 1;
    }
    to {
        transform: translateY(-25%);
        opacity: 0;
    }
`;

const modalWrapperShouldForward = (prop: string) =>
    prop !== 'isVisible' && prop !== 'fullViewport' && prop !== 'centered';

const ModalWrapper = styled('div', {
    shouldForwardProp: modalWrapperShouldForward,
})<{ isVisible: boolean; fullViewport?: boolean; centered?: boolean }>(
    ({ theme, isVisible, fullViewport, centered }) => ({
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: centered ? 'center' : 'flex-start',
        justifyContent: 'center',
        zIndex: theme.zIndex.modal,
        overflowY: 'auto',
        visibility: isVisible ? 'visible' : 'hidden',
        ...(fullViewport && {
            alignItems: 'stretch',
            overflowY: 'hidden',
            boxSizing: 'border-box',
            padding: 24,
        }),
    })
);

const modalContentShouldForward = (prop: string) =>
    prop !== 'show' && prop !== 'fullViewport' && prop !== 'centered';

const ModalContent = styled('div', {
    shouldForwardProp: modalContentShouldForward,
})<{ show: boolean; fullViewport?: boolean; centered?: boolean }>(
    ({ theme, show, fullViewport, centered }) => ({
        position: 'relative',
        background: theme.color.white,
        width: '600px',
        maxWidth: '90%',
        height: 'auto',
        minHeight: 'auto',
        borderRadius: `${theme.borderRadius.base}px`,
        margin: centered ? 'auto' : '30px auto',
        boxShadow: '0 4px 10px rgba(0, 0, 0, 0.3)',
        zIndex: theme.zIndex.modal,
        overflow: 'visible',
        maxHeight: 'none',
        flex: 'none',
        animation: `${show ? slideDown : slideUp} 0.3s ease-out forwards`,
        ...(fullViewport && {
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            minHeight: 0,
            margin: 0,
            overflow: 'hidden',
            flex: '1 1 auto',
        }),
    })
);

const Modal: FC<{
    show?: boolean;
    onHide?: () => void;
    onEnter?: () => void;
    children?: React.ReactNode;
    className?: string;
    fullViewport?: boolean;
    centered?: boolean;
}> = ({
    show = false,
    onHide = noop,
    onEnter,
    children,
    className,
    fullViewport = false,
    centered = false,
}) => {
    const [isVisible, setIsVisible] = useState(show);
    const [isAnimating, setIsAnimating] = useState(false);
    const modalRef = useRef<HTMLDivElement | null>(null);

    const handleAnimationEnd = useCallback(() => !show && setIsAnimating(false), [show]);

    useEffect(() => {
        if (show) {
            setIsVisible(true);
            setIsAnimating(true);
            document.body.style.overflow = 'hidden';
            onEnter?.();
        } else if (isVisible) {
            setIsAnimating(true);
            requestAnimationFrame(() => {
                setTimeout(() => {
                    setIsAnimating(false);
                    setIsVisible(false);
                    document.body.style.overflow = '';
                }, 300);
            });
        }
    }, [show, onEnter]);

    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && show) onHide();
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [show, onHide]);

    if (!isVisible && !isAnimating) return null;

    const modalRoot = document.getElementById('popover-root');

    if (!modalRoot) return null;

    return ReactDOM.createPortal(
        <ModalProvider onHide={onHide}>
            <Backdrop isVisible={show || isAnimating} onClick={show ? onHide : undefined} />
            <ModalWrapper
                isVisible={show || isAnimating}
                fullViewport={fullViewport}
                centered={centered}
                data-testid="modal-wrapper"
            >
                <ModalContent
                    ref={modalRef}
                    className={className}
                    fullViewport={fullViewport}
                    centered={centered}
                    show={show}
                    onAnimationEnd={handleAnimationEnd}
                    data-testid="modal-content"
                >
                    {children}
                </ModalContent>
            </ModalWrapper>
        </ModalProvider>,
        modalRoot
    );
};

export default Modal;
